/**
 * Evidence shortcuts for the Work composer "/" picker — same saved + marketplace
 * list as the floating Ask Heidi / Chat protocols menu.
 */

import { Region } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { SearchShortcut } from '../../../contracts/work-host-shapes';
import { composerLinkedItemsAtom } from '../../../data/projects/artifacts/state';

import { getWorkHostPort } from '../../work-home/work-home-ports';

export function useComposerEvidenceShortcuts(): {
  enabled: boolean;
  shortcuts: readonly SearchShortcut[];
} {
  const evidence = getWorkHostPort('evidence');
  const shell = getWorkHostPort('shell');
  void shell.useIsAskHeidiSourcesShortcutsEnabled();
  // The legacy shortcuts flag is a constant true.
  const shortcutsFeatureEnabled = true;

  const { pinnedChips } = evidence.useEvidenceChipsConfig();
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const hasLinkedContext = linkedItems.length > 0;

  const { data: kindeProperties } = shell.useQueryKindeProperties();
  const isUS = kindeProperties?.region === Region.US;
  const intl = useIntl();

  const marketShortcuts = useMemo(
    () =>
      shortcutsFeatureEnabled ? evidence.getResolvedShortcutsForMarket(isUS, intl) : [],
    [evidence, shortcutsFeatureEnabled, intl, isUS]
  );

  const visiblePinnedChips = useMemo(() => {
    if (!shortcutsFeatureEnabled) return [];
    // Match ChatInput: out-of-session with no linked patient/session surfaces
    // team shortcuts only — personal pins may assume consultation context.
    if (!hasLinkedContext) {
      return pinnedChips.filter((chip) => Boolean(chip.teamShortcutId));
    }
    return pinnedChips;
  }, [shortcutsFeatureEnabled, hasLinkedContext, pinnedChips]);

  const shortcuts = useMemo(
    () =>
      evidence.buildSearchableShortcuts(
        visiblePinnedChips,
        marketShortcuts
      ) as readonly SearchShortcut[],
    [evidence, visiblePinnedChips, marketShortcuts]
  );

  return useMemo(
    () => ({
      enabled: shortcutsFeatureEnabled && shortcuts.length > 0,
      shortcuts,
    }),
    [shortcutsFeatureEnabled, shortcuts]
  );
}
